import React, { useState, useEffect, useRef } from 'react';

const COMMON_UNITS = [
    'adet', 'su bardağı', 'yemek kaşığı', 'tatlı kaşığı', 'çay kaşığı',
    'çay bardağı', 'yarım ekmek', 'dilim', 'fincan', 'kase', 'kepçe', 'tane', 'paket', 'diş',
    'gr', 'gram', 'kg', 'ml', 'lt', 'demet', 'bağ', 'dal', 'tutam'
];

const CANONICAL_CREATE_UNITS = ['g', 'ml', 'adet'];

const TURKISH_ICECEK_LABEL = '\u0130\u00e7ecek';

const normalizeFamilyLabel = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return 'Genel Aile';
    const lower = raw.toLowerCase();
    if (
        lower === 'icecek' ||
        lower === 'i\u00e7ecek' ||
        lower === 'i?ecek' ||
        lower === '??ecek' ||
        raw.includes('??ecek')
    ) {
        return TURKISH_ICECEK_LABEL;
    }
    return raw;
};

const getAdminHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const COMMON_FRACTIONS = [
    { label: '¼', val: '0.25' },
    { label: '½', val: '0.5' },
    { label: '¾', val: '0.75' },
    { label: '1', val: '1' },
    { label: '1.5', val: '1.5' },
    { label: '2', val: '2' },
    { label: '3', val: '3' }
];

const getResolutionState = (res = {}, ing = {}) => {
    const status = String(res?.status || '').toUpperCase();
    const confidence = Number(res?.confidence ?? 0);
    const hasDictionaryId = Boolean(res?.dictionaryId || (ing?.dictionaryId && ing.dictionaryId !== 'unknown'));
    const isFound = Boolean(res?.found || hasDictionaryId);
    const hasCandidates = Array.isArray(res?.candidates) && res.candidates.length > 0;
    const needsVerification = status === 'AMBIGUOUS' || hasCandidates || (confidence > 0 && confidence < 0.92);

    if (!isFound) {
        return {
            key: 'failed',
            icon: 'error',
            statusIcon: 'help',
            dotClass: 'bg-rose-500',
            badgeClass: 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800',
            label: 'Eşleşme başarısız',
            hint: 'Malzeme sözlükte bulunamadı.'
        };
    }

    if (needsVerification) {
        return {
            key: 'needs_verification',
            icon: 'warning',
            statusIcon: 'priority_high',
            dotClass: 'bg-amber-500',
            badgeClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
            label: 'Doğrulama gerekli',
            hint: 'Eşleşme bulundu ama güven bandı düşük.'
        };
    }

    return {
        key: 'matched',
        icon: 'check_circle',
        statusIcon: 'verified',
        dotClass: 'bg-emerald-500',
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800',
        label: 'Eşleşti',
        hint: 'Dictionary kimliği doğrulandı.'
    };
};

/**
 * IngredientResolverTable - BBTV V22
 * Manages identity resolution for recipe ingredients.
 * 
 * @param {Array} ingredients - List of ingredient objects from editingDish
 * @param {Array} resolutions - List of resolution results from backend
 * @param {Function} onResolve - Callback when an ingredient is manually resolved
 * @param {number} previewServings - Current servings count for preview
 * @param {number} originalServings - Original servings count of the recipe
 */
const IngredientResolverTable = ({ ingredients = [], resolutions = [], onResolve, apiBaseUrl, previewServings = 1, originalServings = 1 }) => {
    const [modalData, setModalData] = useState(null);
    const [altModalData, setAltModalData] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    // [Variant System] Local alternative overrides — immune to parent resolution resets
    // Seeded from ingredients on first render, then managed locally
    const [altOverrides, setAltOverrides] = useState(() => {
        const init = {};
        ingredients.forEach((ing, i) => {
            if (ing.alternativeIds?.length) {
                init[i] = { ids: ing.alternativeIds, names: ing.alternativeNames || [] };
            }
        });
        return init;
    });

    const openSearchModal = (index, query, unitHint) => {
        setModalData({
            index,
            query: query === 'Bilinmiyor' ? '' : query,
            unitHint: String(unitHint || '').trim()
        });
    };

    const getResolutionForIngredient = (ing, idx) => {
        const rowKey = String(ing?.rowKey || '').trim();
        if (rowKey) {
            const byRowKey = (Array.isArray(resolutions) ? resolutions : []).find(
                (r) => String(r?.rowKey || '').trim() === rowKey
            );
            return byRowKey || {};
        }
        return (Array.isArray(resolutions) ? resolutions[idx] : null) || {};
    };

    return (
        <div className="space-y-4">
            <div className="overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/40">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-900/60">
                        <tr>
                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest w-16 text-center">Durum</th>
                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[240px]">Değerler (Miktar / Birim)</th>
                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Eşleşen Ürün & Aile</th>
                            <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">İşlem</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {ingredients.map((ing, idx) => {
                            const res = getResolutionForIngredient(ing, idx);
                            const resolutionState = getResolutionState(res, ing);
                            const isFound = resolutionState.key !== 'failed';
                            const matchedName = res.canonicalName || ing.ingredient?.name || ing.name || 'Bilinmiyor';

                            const scaleFactor = previewServings / originalServings;
                            const originalQty = parseFloat(ing.quantity) || 0;
                            const scaledQty = (originalQty * scaleFactor).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
                            const showScaling = previewServings !== originalServings;

                            const mirrorText = String(
                                ing.turkmutfagi ||
                                ((ing.turkmutfagiMiktar || ing.turkmutfagi_miktar) && (ing.turkmutfagiBirim || ing.turkmutfagi_birim)
                                    ? `${ing.turkmutfagiMiktar || ing.turkmutfagi_miktar} ${ing.turkmutfagiBirim || ing.turkmutfagi_birim}`
                                    : '') ||
                                ing.originalText ||
                                ''
                            ).trim();

                            return (
                                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                                    <td className="px-4 py-4 text-center align-top">
                                        <div className={`mt-1 size-6 rounded-full ${resolutionState.dotClass} text-white flex items-center justify-center mx-auto shadow-sm`}>
                                            <span className="material-symbols-outlined text-[14px]">{resolutionState.statusIcon}</span>
                                        </div>
                                        <div className={`mt-2 px-2 py-1 rounded-md border text-[9px] font-black uppercase tracking-widest ${resolutionState.badgeClass}`}>
                                            {resolutionState.label}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 align-top">
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center gap-2">
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={ing.quantity || ''}
                                                        onChange={(e) => onResolve(idx, { quantity: e.target.value })}
                                                        onFocus={() => setEditingCell({ idx, field: 'quantity' })}
                                                        onBlur={() => setTimeout(() => setEditingCell(null), 200)}
                                                        className="w-16 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-black text-primary focus:ring-2 focus:ring-primary/30 outline-none transition-all"
                                                        placeholder="Miktar"
                                                    />
                                                    {editingCell?.idx === idx && editingCell?.field === 'quantity' && (
                                                        <div className="absolute flex flex-wrap gap-1 p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl mt-1 z-[60] w-40 animate-in zoom-in-95 duration-200">
                                                            {COMMON_FRACTIONS.map(f => (
                                                                <button
                                                                    key={f.label}
                                                                    type="button"
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        onResolve(idx, { quantity: f.val });
                                                                    }}
                                                                    className="px-2 py-1 hover:bg-primary hover:text-white rounded bg-slate-100 dark:bg-slate-900 text-[10px] font-bold transition-colors"
                                                                >
                                                                    {f.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="relative group/unit flex-1">
                                                    <select
                                                        value={ing.unit || 'adet'}
                                                        onChange={(e) => onResolve(idx, { unit: e.target.value })}
                                                        className="w-full px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary/30 outline-none appearance-none cursor-pointer"
                                                    >
                                                        {COMMON_UNITS.map(u => (
                                                            <option key={u} value={u}>{u}</option>
                                                        ))}
                                                        {!COMMON_UNITS.includes(ing.unit) && ing.unit && (
                                                            <option value={ing.unit}>{ing.unit}</option>
                                                        )}
                                                    </select>
                                                    <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">unfold_more</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-0.5 ml-1">
                                                <div className="flex items-center gap-2 group/mirror">
                                                    <span className="material-symbols-outlined text-[10px] text-slate-400 group-hover/mirror:text-primary transition-colors">auto_awesome</span>
                                                    <input
                                                        type="text"
                                                        value={mirrorText}
                                                        onChange={(e) => onResolve(idx, { turkmutfagi: e.target.value })}
                                                        className="flex-1 bg-transparent border-b border-dashed border-slate-200 dark:border-slate-700 text-[10px] font-medium text-slate-500 dark:text-slate-400 focus:text-primary focus:border-primary outline-none py-1 transition-all"
                                                        placeholder="Ayna Metni (Örn: Yarım adet...)"
                                                    />
                                                </div>
                                                {showScaling && (
                                                    <div className="flex items-center gap-2 px-2 py-1 bg-primary/5 dark:bg-primary/20 rounded-md border border-primary/20">
                                                        <span className="material-symbols-outlined text-primary text-[12px]">rebase_edit</span>
                                                        <span className="text-[10px] font-bold text-primary">
                                                            {scaledQty} {ing.unit} <span className="text-[9px] font-medium opacity-70">({previewServings} porsiyon)</span>
                                                        </span>
                                                    </div>
                                                )}
                                                <p className="text-[10px] font-medium text-slate-400 font-mono italic">
                                                    Girdi: "{String(res.rawInput || ing.rawInput || 'Manuel')}"
                                                </p>

                                                {/* [Variant System] alternativeIds chip editor */}
                                                <div className="mt-2 pt-2 border-t border-dashed border-slate-100 dark:border-slate-800">
                                                    <div className="flex items-center gap-1 mb-1.5">
                                                        <span className="material-symbols-outlined text-[10px] text-indigo-400">alt_route</span>
                                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Alternatif Malzemeler</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setAltModalData({ index: idx })}
                                                            className="ml-auto size-5 flex items-center justify-center rounded-md bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-800/40 text-indigo-500 transition-colors"
                                                            title="Alternatif ekle"
                                                        >
                                                            <span className="material-symbols-outlined text-[10px]">add</span>
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(altOverrides[idx]?.ids || res.alternativeIds || ing.alternativeIds || []).map((altId, aIdx) => {
                                                            const altNames = altOverrides[idx]?.names || res.alternativeNames || ing.alternativeNames || [];
                                                            const altName = altNames[aIdx] || String(altId);
                                                            return (
                                                                <div key={altId} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/60 rounded-md">
                                                                    <span className="text-[9px] font-bold text-indigo-600 dark:text-indigo-300">{altName}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const currentIds = altOverrides[idx]?.ids || res.alternativeIds || ing.alternativeIds || [];
                                                                            const currentNames = altOverrides[idx]?.names || res.alternativeNames || ing.alternativeNames || [];
                                                                            const newIds = currentIds.filter((_, i) => i !== aIdx);
                                                                            const newNames = currentNames.filter((_, i) => i !== aIdx);
                                                                            setAltOverrides(prev => ({ ...prev, [idx]: { ids: newIds, names: newNames } }));
                                                                            onResolve(idx, { alternativeIds: newIds, alternativeNames: newNames });
                                                                        }}
                                                                        className="size-3 flex items-center justify-center rounded-full hover:bg-rose-200 dark:hover:bg-rose-800/50 hover:text-rose-500 transition-colors"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[8px]">close</span>
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                        {!(altOverrides[idx]?.ids?.length || res.alternativeIds?.length || ing.alternativeIds?.length) && (
                                                            <span className="text-[9px] text-slate-300 dark:text-slate-600 italic">Alternatif yok — + ile ekleyin</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div
                                            role="button"
                                            className="group relative flex items-center gap-2 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                                            onClick={() => openSearchModal(idx, matchedName, ing.unit || ing.birim || 'g')}
                                        >
                                            <div className="flex flex-col flex-1 overflow-hidden">
                                                <span className={`text-xs font-black uppercase truncate ${isFound ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}`}>
                                                    {String(matchedName)}
                                                </span>
                                                {isFound && (
                                                    <span className="text-[9px] text-slate-400 font-medium truncate italic">
                                                        {normalizeFamilyLabel(res.familyName || ing.familyName || 'Aile Belirtilmemi\u015f')}
                                                    </span>
                                                )}
                                                <span className={`text-[9px] font-semibold ${resolutionState.key === 'matched' ? 'text-emerald-500' : resolutionState.key === 'needs_verification' ? 'text-amber-500' : 'text-rose-500'}`}>
                                                    {resolutionState.hint}
                                                </span>
                                            </div>
                                            <span className="material-symbols-outlined text-slate-300 group-hover:text-primary transition-colors text-lg opacity-0 group-hover:opacity-100">edit</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                        <button
                                            onClick={() => openSearchModal(idx, matchedName, ing.unit || ing.birim || 'g')}
                                            type="button"
                                            className="size-8 flex items-center justify-center hover:bg-primary/10 rounded-lg transition-colors text-slate-400 hover:text-primary active:scale-95"
                                            title="Eşleşmeyi Düzenle"
                                        >
                                            <span className="material-symbols-outlined text-lg">search_insights</span>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {modalData && (
                <IngredientSearchModal
                    idx={modalData.index}
                    initialQuery={modalData.query}
                    initialUnit={modalData.unitHint}
                    apiBaseUrl={apiBaseUrl}
                    onClose={() => setModalData(null)}
                    onSelect={(product) => {
                        onResolve(modalData.index, {
                            dictionaryId: product.id,
                            canonicalName: product.name,
                            familyName: normalizeFamilyLabel(product.family),
                            found: true
                        });
                        setModalData(null);
                    }}
                />
            )}

            {/* [Variant System] Alternative picker modal */}
            {altModalData && (
                <IngredientSearchModal
                    idx={altModalData.index}
                    initialQuery=""
                    apiBaseUrl={apiBaseUrl}
                    onClose={() => setAltModalData(null)}
                    onSelect={(product) => {
                        const idx = altModalData.index;
                        const currentLocal = altOverrides[idx] || { ids: [], names: [] };
                        const idStr = String(product.id);
                        console.log('[AltPicker] product:', product.name, 'id:', idStr, 'currentLocal.ids:', currentLocal.ids);
                        if (!currentLocal.ids.includes(idStr)) {
                            const newIds = [...currentLocal.ids, idStr];
                            const newNames = [...currentLocal.names, product.name];
                            // Update local state immediately (immune to parent resets)
                            setAltOverrides(prev => ({ ...prev, [idx]: { ids: newIds, names: newNames } }));
                            // Also persist to parent for DB save
                            onResolve(idx, { alternativeIds: newIds, alternativeNames: newNames });
                        }
                        setAltModalData(null);
                    }}
                />
            )}

            {!ingredients.length && (
                <div className="text-center py-10 bg-slate-50 dark:bg-slate-900/20 rounded-2xl border-2 border-dashed border-slate-100 dark:border-slate-800 text-slate-400 italic text-xs">
                    Henüz malzeme eklenmemiş.
                </div>
            )}
        </div>
    );
};

/**
 * High-End Ingredient Search & Creation Modal
 */
const IngredientSearchModal = ({ idx, initialQuery, initialUnit, apiBaseUrl, onClose, onSelect }) => {
    const [q, setQ] = useState(initialQuery);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [step, setStep] = useState(1); // 1: Search Product, 2: Select Family, 3: Product Details
    const [familyQ, setFamilyQ] = useState('');
    const [familyResults, setFamilyResults] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [createName, setCreateName] = useState(String(initialQuery || '').trim());
    const [createUnit, setCreateUnit] = useState(String(initialUnit || 'g').toLowerCase() || 'g');

    const inferFamilyHint = (name) => {
        const q = String(name || '').toLocaleLowerCase('tr-TR');
        if (q.includes('kuzu') || q.includes('dana') || q.includes('et') || q.includes('incik') || q.includes('pirzola')) return 'et';
        if (q.includes('tavuk') || q.includes('hindi')) return 'tavuk';
        if (q.includes('balık') || q.includes('somon') || q.includes('hamsi')) return 'balık';
        return '';
    };

    useEffect(() => {
        if (initialQuery) handleSearch(initialQuery);
    }, []);

    useEffect(() => {
        if (step !== 2 || selectedFamily) return;
        const hint = inferFamilyHint(q);
        if (!hint) return;
        handleFamilySearch(hint);
    }, [step]);

    useEffect(() => {
        if (step === 3 && !createName.trim()) {
            setCreateName(String(q || '').trim());
        }
    }, [step, q, createName]);

    const handleSearch = async (val) => {
        setQ(val);
        if (val.length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${apiBaseUrl}/api/inventory/search-ontology?q=${encodeURIComponent(val)}`);
            const data = await res.json();
            setResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFamilySearch = async (val) => {
        setFamilyQ(val);
        if (val.length < 2) {
            setFamilyResults([]);
            return;
        }
        try {
            const res = await fetch(`${apiBaseUrl}/api/admin/families/search?q=${encodeURIComponent(val)}`, {
                credentials: 'include',
                headers: getAdminHeaders()
            });
            const data = await res.json();
            if (data.ok) setFamilyResults(data.families);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreateNew = async () => {
        const finalName = String(createName || q || '').trim();
        if (!finalName || finalName.length < 2) return;
        if (!selectedFamily?.familyKey || !selectedFamily?.familyName) {
            alert('Lütfen önce bir aile seçin.');
            return;
        }
        setIsCreating(true);
        try {
            const res = await fetch(`${apiBaseUrl}/api/admin-recipes/create-dictionary-entry`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
                body: JSON.stringify({
                    rawName: finalName,
                    familyKey: selectedFamily?.familyKey,
                    familyName: selectedFamily?.familyName,
                    canonicalUnit: createUnit || 'g'
                })
            });
            const data = await res.json();
            if (data.ok) {
                onSelect({
                    id: data.dictionaryId,
                    name: data.entry.canonicalName,
                    family: data.entry.familyName
                });
            } else {
                alert(data.message || 'Hata oluştu');
            }
        } catch (err) {
            alert('Ağ hatası oluştu');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Modal Header */}
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
                    <div>
                        <h3 className="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary">manage_search</span>
                            Malzeme Kimliği Tanımla
                        </h3>
                        <p className="text-xs text-slate-500 font-medium">Reçetedeki malzemeyi sistem sözlüğü ile eşleştirin.</p>
                    </div>
                    <button onClick={onClose} className="size-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-rose-500 hover:text-white transition-all">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Search Body - STEP 1 */}
                {step === 1 && (
                    <div className="p-6 space-y-4">
                        <div className="relative">
                            <input
                                autoFocus
                                type="text"
                                value={q}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-sm font-bold focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all pr-12"
                                placeholder="Malzeme adı (Örn: Domates, Tavuk Göğsü...)"
                            />
                            {loading ? (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                    <div className="size-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                </div>
                            ) : (
                                <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300">search</span>
                            )}
                        </div>

                        <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                            {results.length > 0 ? (
                                results.map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => onSelect(p)}
                                        className="w-full flex items-center justify-between p-4 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all group"
                                    >
                                        <div className="flex flex-col items-start">
                                            <span className="text-sm font-black text-slate-700 dark:text-slate-200 group-hover:text-primary">{p.name}</span>
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{normalizeFamilyLabel(p.family)}</span>
                                        </div>
                                        <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-[10px] font-black text-slate-500 uppercase">{p.type}</span>
                                    </button>
                                ))
                            ) : q.length >= 2 && !loading ? (
                                <div className="p-10 text-center space-y-3">
                                    <div className="size-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto">
                                        <span className="material-symbols-outlined text-3xl text-slate-300">inventory_2</span>
                                    </div>
                                    <p className="text-sm text-slate-500 font-medium">Sözlükte uygun bir eşleşme bulunamadı.</p>
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}

                {/* Family Selection - STEP 2 */}
                {step === 2 && (
                    <div className="p-6 space-y-4 animate-in slide-in-from-right duration-200">
                        <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100 dark:border-emerald-800/50 mb-4">
                            <span className="material-symbols-outlined text-emerald-500">account_tree</span>
                            <div>
                                <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Aile Secimi Zorunlu</p>
                                <p className="text-xs font-bold text-slate-700 dark:text-slate-200">"{q}" icin once hedef aileyi secin.</p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bir Aileye Bağla (Zorunlu)</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={familyQ}
                                    onChange={(e) => handleFamilySearch(e.target.value)}
                                    className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-sm font-bold focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all pr-12"
                                    placeholder="Aile ara (Örn: Şehriye, Un, Tavuk...)"
                                />
                                <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300">category</span>
                            </div>
                        </div>

                        <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar pr-2">
                            {familyResults.map(f => (
                                <button
                                    key={f.familyKey}
                                    onClick={() => setSelectedFamily(f)}
                                    className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${selectedFamily?.familyKey === f.familyKey ? 'border-primary bg-primary/5 dark:bg-primary/20' : 'border-slate-100 dark:border-slate-800 hover:border-primary/30'}`}
                                >
                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{f.familyName}</span>
                                    {selectedFamily?.familyKey === f.familyKey && <span className="material-symbols-outlined text-primary">check</span>}
                                </button>
                            ))}
                            {familyQ.length >= 2 && familyResults.length === 0 && (
                                <div className="p-4 text-center text-xs text-amber-500 italic font-bold">Eşleşen aile bulunamadı. Önce Seeded Veritabanı'ndan aile oluşturun.</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Product Details - STEP 3 */}
                {step === 3 && (
                    <div className="p-6 space-y-4 animate-in slide-in-from-right duration-200">
                        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                            <p className="text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                Seçilen aile: <span className="text-primary">{selectedFamily?.familyName || '-'}</span>
                            </p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Ürün Adı</label>
                            <input
                                type="text"
                                value={createName}
                                onChange={(e) => setCreateName(e.target.value)}
                                className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-sm font-bold focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all"
                                placeholder="Örn: Kuzu İncik"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Temel Birim</label>
                            <select
                                value={createUnit}
                                onChange={(e) => setCreateUnit(e.target.value)}
                                className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-sm font-bold focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all"
                            >
                                {CANONICAL_CREATE_UNITS.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </select>
                            <p className="text-[10px] text-slate-400">Birim otomatik getirildi. Gerekirse burada değiştir.</p>
                        </div>
                    </div>
                )}

                {/* Modal Footer */}
                <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                            {step === 1 ? (
                                q.length >= 2 && (
                                    <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                                        Eger malzeme listede yoksa yeni bir kimlik olusturabilirsiniz.
                                    </p>
                                )
                            ) : (
                                <button
                                    onClick={() => setStep(step === 3 ? 2 : 1)}
                                    className="text-[10px] font-black text-slate-500 hover:text-primary transition-colors flex items-center gap-1 uppercase tracking-widest"
                                >
                                    <span className="material-symbols-outlined text-sm">arrow_back</span> Geri Don
                                </button>
                            )}
                        </div>

                        {step === 1 && (
                            <button
                                disabled={q.length < 2 || loading}
                                onClick={() => setStep(2)}
                                className="flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-primary text-white rounded-2xl text-xs font-black shadow-lg hover:scale-[1.02] active:scale-95 disabled:opacity-50 transition-all"
                            >
                                Yeni Malzeme Olarak Ekle
                                <span className="material-symbols-outlined text-sm">add_circle</span>
                            </button>
                        )}

                        {step === 2 && (
                            <button
                                disabled={!selectedFamily?.familyKey}
                                onClick={() => setStep(3)}
                                className="flex items-center gap-2 px-8 py-4 bg-slate-900 dark:bg-primary text-white rounded-2xl text-xs font-black shadow-xl hover:scale-[1.02] active:scale-95 disabled:opacity-50 transition-all"
                            >
                                Urun Detayina Gec
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        )}

                        {step === 3 && (
                            <button
                                disabled={isCreating || !selectedFamily?.familyKey || String(createName || '').trim().length < 2}
                                onClick={handleCreateNew}
                                className="flex items-center gap-2 px-8 py-4 bg-emerald-600 dark:bg-emerald-500 text-white rounded-2xl text-xs font-black shadow-xl shadow-emerald-500/20 hover:scale-[1.02] active:scale-95 disabled:opacity-50 transition-all"
                            >
                                {isCreating ? 'Kaydediliyor...' : 'Kesinlestir ve Ekle'}
                                {!isCreating && <span className="material-symbols-outlined text-sm">done_all</span>}
                            </button>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IngredientResolverTable;
